import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "../utils";
import { Favorite, User, Content } from "../types";
import { motion } from "framer-motion";
import { Heart, Play, Wind, Droplets, TreePine, Flame } from "lucide-react";
import { useAppSettings } from "../components/providers/AppSettingsProvider";
import { getPageBackgroundGradient } from "../config/theme";
import { soundThemes, WHITE_NOISE_IDS, MEDITATION_IDS } from "../config/soundContent";
import LibraryAnalysisContent from "../components/library/LibraryAnalysisContent";
import LibraryKnowYourselfContent from "../components/library/LibraryKnowYourselfContent";

const WHITE_NOISE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  rain: Droplets,
  forest: TreePine,
  wind: Wind,
  fireplace: Flame
};

export type LibraryTabId = 'meditation' | 'analysis' | 'knowYourself';

/** Page scroll order (must match tab order and section ids). */
const LIBRARY_SCROLL_SECTION_ORDER: LibraryTabId[] = ['analysis', 'knowYourself', 'meditation'];

const ContentLibrary: React.FC = () => {
  const location = useLocation();
  const { t, theme } = useAppSettings();
  const isDark = theme === 'dark';
  const [activeLibraryTab, setActiveLibraryTab] = useState<LibraryTabId>('analysis');
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    loadData();
  }, [location]);

  const loadData = async () => {
    try {
      // 模拟用户数据加载
      const mockUser: User = {
        id: '1',
        email: 'user@example.com',
        full_name: '用户',
        total_sessions: 15,
        total_minutes: 450,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUser(mockUser);
      // 模拟收藏数据
      setFavorites([]);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const whiteNoiseContent: Content[] = useMemo(() => {
    return WHITE_NOISE_IDS.map((id) => {
      const theme = soundThemes[id];
      return {
        id,
        name: t(theme.title),
        description: t(theme.description),
        cover: theme.backgroundImage,
        type: 'white_noise' as const,
        icon: WHITE_NOISE_ICONS[id],
        audioSrc: theme.audioSrc ?? undefined,
        duration: theme.duration
      };
    });
  }, [t]);

  const meditationContent: Content[] = useMemo(() => {
    return MEDITATION_IDS.map((id) => {
      const theme = soundThemes[id];
      return {
        id,
        name: t(theme.title),
        description: t(theme.description),
        cover: theme.backgroundImage,
        type: 'meditation' as const,
        audioSrc: theme.audioSrc ?? undefined,
        duration: theme.duration
      };
    });
  }, [t]);

  const librarySections = useMemo(
    () => [
      { id: 'sounds' as const, title: t('library.sounds'), items: whiteNoiseContent },
      { id: 'meditation' as const, title: t('library.meditation'), items: meditationContent },
    ],
    [t, whiteNoiseContent, meditationContent]
  );

  const libraryTabs = useMemo(
    () =>
      [
        { id: 'analysis' as const, label: t('library.tab.analysis') },
        { id: 'knowYourself' as const, label: t('library.tab.knowYourself') },
        { id: 'meditation' as const, label: t('library.meditation') },
      ] satisfies { id: LibraryTabId; label: string }[],
    [t]
  );

  const scrollToLibrarySection = useCallback((tabId: LibraryTabId) => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`#library-section-${tabId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const updateActiveTabFromScroll = useCallback(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    const probeY = rootRect.top + Math.min(100, rootRect.height * 0.12);
    let next: LibraryTabId | null = null;
    for (let i = LIBRARY_SCROLL_SECTION_ORDER.length - 1; i >= 0; i--) {
      const id = LIBRARY_SCROLL_SECTION_ORDER[i];
      const el = root.querySelector<HTMLElement>(`#library-section-${id}`);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.top <= probeY) {
        next = id;
        break;
      }
    }
    if (next) {
      setActiveLibraryTab((prev) => (prev === next ? prev : next));
    }
  }, []);

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const onScroll = () => {
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        updateActiveTabFromScroll();
      });
    };
    updateActiveTabFromScroll();
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [updateActiveTabFromScroll]);

  const isFavorite = (contentId: string): boolean => {
    return favorites.some(fav => fav.content_id === contentId);
  };

  const toggleFavorite = async (content: Content) => {
    const existing = favorites.find(fav => fav.content_id === content.id);
    if (existing) {
      // 模拟删除收藏
      setFavorites(prev => prev.filter(f => f.id !== existing.id));
    } else {
      // 模拟添加收藏
      const newFav: Favorite = {
        id: `fav_${Date.now()}`,
        content_type: content.type,
        content_id: content.id,
        content_name: content.name,
        content_description: content.description,
        duration_minutes: content.duration || 0, // 使用默认值0，因为duration现在是可选的
        created_by: user?.email || '',
        created_at: new Date().toISOString()
      };
      setFavorites(prev => [...prev, newFav]);
    }
  };

  return (
    <div className="fixed inset-0 w-full h-screen overflow-hidden flex flex-col">
      {/* 背景渐变：使用统一配置 */}
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />
      
      {/* Fixed Header Section */}
      <div className="relative z-10 flex-shrink-0">
        {/* Header */}
        <div className="px-6 pb-4" style={{ paddingTop: '10vh' }}>
          <h1 className={`text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {t('library.title')}
          </h1>
        </div>

        <div className="px-6 pb-4">
          <div className="flex gap-2">
            {libraryTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => scrollToLibrarySection(tab.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all backdrop-blur-md border ${
                  activeLibraryTab === tab.id
                    ? isDark
                      ? 'bg-white/20 text-white border-white/30'
                      : 'bg-white/60 text-gray-800 border-white/70'
                    : isDark
                      ? 'bg-white/10 text-white/80 border-white/20 hover:bg-white/15'
                      : 'bg-white/30 text-gray-700 border-white/50 hover:bg-white/40'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={scrollContainerRef} className="relative z-10 flex-1 overflow-y-auto scroll-smooth">
        <div className="space-y-12 px-6 pb-24 pt-2">
          <section
            id="library-section-analysis"
            className="scroll-mt-2"
            aria-label={t('library.tab.analysis')}
          >
            <LibraryAnalysisContent isDark={isDark} />
          </section>

          <section
            id="library-section-knowYourself"
            className="scroll-mt-2 min-h-[30vh]"
            aria-labelledby="library-heading-knowYourself"
          >
            <LibraryKnowYourselfContent isDark={isDark} />
          </section>

          <section
            id="library-section-meditation"
            className="scroll-mt-2"
            aria-label={t('library.meditation')}
          >
            {librarySections.map((section, sectionIndex) => {
              const itemStart = librarySections
                .slice(0, sectionIndex)
                .reduce((sum, s) => sum + s.items.length, 0);
              return (
                <div key={section.id} className={sectionIndex > 0 ? 'mt-10' : ''}>
                  <h2
                    className={`mb-4 text-lg font-semibold tracking-tight ${
                      isDark ? 'text-white' : 'text-gray-800'
                    }`}
                  >
                    {section.title}
                  </h2>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                    {section.items.map((content, index) => {
                      const globalIndex = itemStart + index;
                      return (
                        <motion.div
                          key={content.id}
                          initial={{ opacity: 0, scale: 0.95, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ delay: globalIndex * 0.05 }}
                        >
                          <Link
                            to={
                              createPageUrl('AudioPlayer') +
                              `?content=${content.id}&type=${content.type}`
                            }
                          >
                            <div className="group relative">
                              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                                <img
                                  src={content.cover}
                                  alt={content.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-xl backdrop-blur-sm">
                                    <Play className="ml-0.5 h-5 w-5 fill-black text-black" />
                                  </div>
                                </div>

                                <div className="absolute bottom-0 left-0 right-0 p-3 pb-4">
                                  <h3
                                    className={`mb-0.5 line-clamp-1 text-sm font-medium ${
                                      isDark ? 'text-white' : 'text-gray-800'
                                    }`}
                                  >
                                    {content.name}
                                  </h3>
                                  <p
                                    className={`line-clamp-1 text-xs font-light ${
                                      isDark ? 'text-white/80' : 'text-gray-700'
                                    }`}
                                  >
                                    {content.description}
                                  </p>
                                </div>

                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleFavorite(content);
                                  }}
                                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-md transition-colors hover:bg-white/30"
                                >
                                  <Heart
                                    className={`h-4 w-4 ${
                                      isFavorite(content.id)
                                        ? isDark
                                          ? 'fill-[#ffffff] text-[#ffffff]'
                                          : 'fill-[#587FFF] text-[#587FFF]'
                                        : isDark
                                          ? 'text-white'
                                          : 'text-[#587FFF]'
                                    }`}
                                    strokeWidth={1.5}
                                  />
                                </button>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContentLibrary;



